import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import ClientCompanyForm from "../components/ClientCompanyForm";
import PageSectionCard from "../components/PageSectionCard";
import { useAppSession } from "../context/AppSessionContext";
import {
  ClientCompanyFormValues,
  toCreateClientCompanyPayload,
} from "../lib/clientCompanyForm";
import { readErrorMessage } from "../lib/http";

const defaultValues: ClientCompanyFormValues = {
  name: "",
  registrationNumber: "",
  contactEmail: "",
  contactPhone: "",
  address: "",
};

function ClientCompanyCreatePage() {
  const { authorizedFetch, canManageClientCompanies } = useAppSession();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ClientCompanyFormValues>({ defaultValues });
  const { reset } = form;
  const isSubmitting = form.formState.isSubmitting;
  const formId = "client-company-form";

  const onSubmit = async (values: ClientCompanyFormValues) => {
    if (!canManageClientCompanies) {
      setSubmitError("Only manager/admin users can create client companies.");
      return;
    }

    setSubmitError(null);

    try {
      const response = await authorizedFetch("/client-companies", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(toCreateClientCompanyPayload(values)),
      });

      if (!response.ok) {
        const fallbackMessage =
          response.status === 401 || response.status === 403
            ? "You are not authorized to create client companies."
            : response.status === 409
              ? "A client company with this registration number already exists."
              : `Failed to create client company (${response.status}).`;

        throw new Error(await readErrorMessage(response, fallbackMessage));
      }

      const created = (await response.json()) as { name: string };
      toast.success(`Created client company: ${created.name}`);
      reset(defaultValues);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to create client company.";
      setSubmitError(message);
    }
  };

  return (
    <PageSectionCard
      titleId="setup-title"
      eyebrow="Client Company"
      title="Setup"
      stickyHeader
      actions={
        <button
          className="primaryButton"
          type="submit"
          form={formId}
          disabled={isSubmitting || !canManageClientCompanies}
        >
          {isSubmitting ? "Creating..." : "Create client company"}
        </button>
      }
    >
      <ClientCompanyForm
        id={formId}
        form={form}
        onSubmit={onSubmit}
        submitError={submitError}
        withPlaceholders
      />
    </PageSectionCard>
  );
}

export default ClientCompanyCreatePage;
